/**
 * A3 — Ajustes → Canales: qué canales de comunicación usa el bufete. El correo entra solo
 * desde las cuentas conectadas; el resto se registra a mano (llamadas, reuniones, WhatsApp,
 * cartas, visitas). Lo que NO existe todavía se muestra aparte, con el motivo.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Info, Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { CHANNEL_ICON } from '../../components/communications/common'
import { useAuth } from '../../contexts/AuthContext'
import type {
  CommunicationChannelDto,
  CommunicationChannelKind,
  CommunicationChannelListResponse,
} from '@toga/shared'

function Switch({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (v: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative w-9 h-5 rounded-full transition shrink-0 disabled:opacity-50',
        checked ? 'bg-toga-500' : 'bg-slate-600/60',
      ].join(' ')}
    >
      <span
        className={[
          'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all',
          checked ? 'left-[18px]' : 'left-0.5',
        ].join(' ')}
      />
    </button>
  )
}

export default function ChannelsSettingsPage() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const [data, setData] = useState<CommunicationChannelListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setData(await api.get<CommunicationChannelListResponse>('/communication-channels'))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los canales')
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  const update = async (
    kind: CommunicationChannelKind,
    body: { enabled?: boolean; autoAssign?: boolean }
  ) => {
    setBusy(`${kind}:${Object.keys(body)[0]}`)
    setError(null)
    try {
      await api.patch(`/communication-channels/${kind}`, body)
      await load()
    } catch (err) {
      const b = err instanceof ApiError ? (err.body as { error?: string } | undefined) : undefined
      setError(b?.error ?? (err instanceof Error ? err.message : 'No se pudo guardar'))
    } finally {
      setBusy(null)
    }
  }

  const row = (c: CommunicationChannelDto) => (
    <li
      key={c.kind}
      className="px-5 py-4 flex flex-wrap items-start gap-4 border-b border-base/50 last:border-b-0"
    >
      <span className="w-9 h-9 rounded-lg grid place-items-center bg-toga-500/15 text-toga-300 shrink-0">
        {CHANNEL_ICON[c.kind]}
      </span>
      <div className="min-w-0 flex-1 basis-72">
        <div className="flex items-center gap-2">
          <h3 className="t-primary text-sm font-semibold">{c.label}</h3>
          <span
            className={[
              'text-[10px] px-1.5 py-0.5 rounded-full border',
              c.capture === 'AUTOMATIC'
                ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                : 'border-base t-muted',
            ].join(' ')}
          >
            {c.capture === 'AUTOMATIC' ? 'Entra solo' : 'Registro manual'}
          </span>
          <span className="t-faint text-xs">{c.total} registradas</span>
        </div>
        <p className="t-muted text-xs mt-1">{c.description}</p>
        {c.health && (
          <p className="t-secondary text-xs mt-1.5 flex items-center gap-1.5">
            {c.health.withErrors > 0 && <AlertCircle size={12} className="text-amber-400" />}
            {c.health.accounts} cuenta(s) conectada(s)
            {c.health.withErrors > 0 && `, ${c.health.withErrors} con error`}
            {' · '}
            {c.health.lastSyncAt
              ? `última sincronización ${new Date(c.health.lastSyncAt).toLocaleString('es-ES')}`
              : 'aún sin sincronizar'}
          </p>
        )}
        {c.autoAssign !== null && (
          <label className="mt-2 flex items-center gap-2 text-xs t-secondary">
            <Switch
              checked={c.autoAssign}
              disabled={!isAdmin || busy !== null}
              onChange={(v) => void update(c.kind, { autoAssign: v })}
              label="Asignar automáticamente por dirección"
            />
            Asignar solo al cliente cuando la dirección del remitente o de un destinatario coincide
            con la de un único cliente o contacto
          </label>
        )}
      </div>
      <label className="flex items-center gap-2 text-xs t-muted">
        {c.enabled ? 'Activado' : 'Desactivado'}
        <Switch
          checked={c.enabled}
          disabled={!isAdmin || busy !== null}
          onChange={(v) => void update(c.kind, { enabled: v })}
          label={`${c.label} activado`}
        />
      </label>
    </li>
  )

  return (
    <div className="space-y-5 max-w-3xl">
      <header>
        <h2 className="text-xl font-serif font-semibold t-primary">Canales de comunicación</h2>
        <p className="t-muted text-sm mt-1">
          Todo lo que se hable con un cliente, por el canal que sea, queda en la pestaña
          «Comunicación» de su ficha.
          {!isAdmin && ' Solo un administrador puede cambiar estos ajustes.'}
        </p>
      </header>

      {error && (
        <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
          {error}
        </div>
      )}
      {!data && !error && (
        <div className="t-muted text-sm flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando…
        </div>
      )}
      {data && (
        <>
          <ul className="surface overflow-hidden">{data.items.map(row)}</ul>

          <section className="surface p-5 space-y-3">
            <h3 className="t-primary text-sm font-semibold flex items-center gap-2">
              <Info size={14} className="text-toga-300" /> Todavía no disponibles
            </h3>
            {data.unavailable.map((u) => (
              <div key={u.id}>
                <div className="t-secondary text-sm">{u.label}</div>
                <p className="t-muted text-xs mt-0.5">{u.reason}</p>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  )
}
